/* global bocAmchart4, am4core, am4charts */
/* eslint-disable id-length */

/**
 * Draws an arrow and/or line on chart
 *
 * @param {Object} chart - The chart object
 * @param {number|string} startX - starting x-coordinate or date of arrow
 * @param {number} startY - starting y-coordinate of arrow
 * @param {number|string} endX - ending x-coordinate or date of arrow
 * @param {number} endY - ending y-coordinate of arrow
 * @param {string} lineType - Type of line: solid, dashed, dotted
 * @param {(string|number)} lineColor - Color of arrow
 * @param {boolean} hasArrowHead - option for omitting arrow-head (line only)
 * @returns {object} - Reference to series/line
 */
bocAmchart4.addArrow = function (chart, startX, startY, endX, endY, lineType, lineColor, hasArrowHead) {
    // Detect if x values are dates or numbers
    let isDateX = false;

    let parsedStartDate = bocAmchart4.parseIfDate(startX);
    let parsedEndDate = bocAmchart4.parseIfDate(endX);
    if (parsedStartDate && parsedEndDate) {
        isDateX = true;
    }

    // Calculate the angle between the two points
    let angle = (90 - bocAmchart4.calculateAngleDegrees(startX, startY, endX, endY)) % 360;

    // Create a line series
    let lineSeries = chart.series.push(new am4charts.LineSeries());
    lineSeries.name = "arrow";

    // Add data points for the line series
    if (isDateX) {
        lineSeries.data = [
            {date: parsedStartDate, y: startY, bulletVisible: true},
            {date: parsedEndDate, y: endY, bulletVisible: !hasArrowHead}
        ];
    } else {
        lineSeries.data = [
            {x: startX, y: startY, bulletVisible: true},
            {x: endX, y: endY, bulletVisible: !hasArrowHead}
        ];
    }

    // Validate color input
    let color;
    if (typeof lineColor === 'number' && lineColor >= 0 && lineColor <= bocAmchart4.bocColors.length) {
        color = chart.colors.getIndex(lineColor);
    } else if (typeof lineColor === 'string') {
        color = lineColor;
    } else {
        //default color
        color = chart.colors.getIndex(30);
    }

    // Configure the line series
    if (isDateX) {
        lineSeries.dataFields.dateX = "date";
    } else {
        lineSeries.dataFields.valueX = "x";
    }
    lineSeries.dataFields.valueY = "y";
    lineSeries.stroke = am4core.color(color);
    lineSeries.events.once("inited", function () {
        lineSeries.parent.zIndex = 10;
    });

    // Set line type
    switch (lineType) {
        case "solid":
            lineSeries.strokeDasharray = "";
            break;
        case "dashed":
            lineSeries.strokeDasharray = "4,4";
            break;
        case "dotted":
            lineSeries.strokeDasharray = "1,1";
            break;
        default:
            lineSeries.strokeDasharray = lineType;
    }

    // Add a bullet (triangle arrow) at the end of the line series
    let arrowBullet = lineSeries.bullets.push(new am4charts.Bullet());
    arrowBullet.propertyFields.disabled = "bulletVisible";

    let triangle = arrowBullet.createChild(am4core.Triangle);
    triangle.horizontalCenter = "middle";
    triangle.verticalCenter = "bottom";

    triangle.width = 6;
    triangle.height = 10;
    triangle.fill = am4core.color(color);
    arrowBullet.rotation = angle;

    lineSeries.hiddenInLegend = true;
    lineSeries.arrowBullet = arrowBullet;

    // Adjust zIndex so arrows are on top
    lineSeries.zIndex = 100;
    arrowBullet.zIndex = 101;
    triangle.zIndex = 102;

    //this function will recalculate the arrow head rotation but based on the
    //point system of the chart
    let updateArrowRotation = () => {
        // Get the pixel positions of the start and end data points
        let lineStartPoint = lineSeries.dataItems.getIndex(0);
        let lineEndPoint = lineSeries.dataItems.getIndex(1);

        // some edge case checking
        if (!lineStartPoint || !lineEndPoint) {
            return;
        }
        if (!lineStartPoint.point || !lineEndPoint.point) {
            return;
        }

        // get the new line points but in terms of the chart coordinate system
        let startPoint = lineStartPoint.point;
        let endPoint = lineEndPoint.point;
        let dx = endPoint.x - startPoint.x;
        let dy = endPoint.y - startPoint.y;

        // Compute new angle to set the arrow head to when the line changes
        let screenAngle = Math.atan2(dy, dx) * (180 / Math.PI);

        //change the rotation
        arrowBullet.rotation = screenAngle + 90;

        // Offset the bullet so the triangle tip is exactly at the endpoint
        let length = Math.sqrt(dx * dx + dy * dy);
        if (length > 0) {
            // Move back by triangle height
            let offset = triangle.height;
            arrowBullet.dx = -offset * dx / length;
            arrowBullet.dy = -offset * dy / length;
        }
    };
    // Update arrow rotation whenever the chart is updated
    chart.events.on("zoomended", updateArrowRotation);
    arrowBullet.events.on("positionchanged", updateArrowRotation);
    return lineSeries;
};

/**
 * Helper function that calculates angle for arrow head
 *
 * @param {number} x1 - starting x-coordinate of arrow
 * @param {number} y1 - starting y-coordinate of arrow
 * @param {number} x2 - ending x-coordinate of arrow
 * @param {number} y2 - ending y-coordinate of arrow
 * @returns {number} The calculated arrowHead angle
 */
bocAmchart4.calculateAngleDegrees = function (x1, y1, x2, y2) {
    let angleRad = Math.atan2(y2 - y1, x2 - x1);
    let angleDeg = angleRad * (180 / Math.PI);

    if (angleDeg < 0) {
        angleDeg += 360;
    }

    return angleDeg;
};

/**
 * Adds a label(s) to chart
 *
 * @param {Object} chart - The chart object
 * @param {Object[]} labels - An array of objects containing label properties
 * @param {number|string} labels.x - x-coordinate or date of label
 * @param {number} labels.y - y-coordinate of label
 * @param {string} labels.textAlignment - text alignment
 * @param {string} labels.horizontalCenter - which part of label to position on coordinate (left, right, middle)
 * @param {string} labels.text - label text
 * @returns {void}
 */
bocAmchart4.addLabels = function (chart, labels) {
    // Detect if all x values are dates
    let allDates = labels.length > 0 && labels.every(label => bocAmchart4.parseIfDate(label.x));

    let newSeries = chart.series.push(new am4charts.XYSeries());
    newSeries.name = "chartLabels";
    if (allDates) {
        newSeries.dataFields.dateX = "date";
    } else {
        newSeries.dataFields.valueX = "x";
    }
    newSeries.dataFields.valueY = "y";
    newSeries.hiddenInLegend = true;

    newSeries.data = labels.map(label => {
        let dataPoint = {
            labelText: label.text,
            y: label.y
        };

        // Only add alignment properties if they are provided
        if (label.textAlignment) {
            dataPoint.labelTextAlignment = label.textAlignment;
        }
        if (label.horizontalCenter) {
            dataPoint.labelHorizontalCenter = label.horizontalCenter;
        }

        if (allDates) {
            dataPoint.date = bocAmchart4.parseIfDate(label.x);
        } else {
            dataPoint.x = label.x;
        }
        return dataPoint;
    });

    // wait for chart to be initialized, then add labels
    newSeries.events.on("inited", () => {
        let bullet = newSeries.bullets.push(new am4charts.LabelBullet());
        bullet.label.text = "{labelText}";

        // Add adapters with defensive checks for dataItem/dataContext
        bullet.label.adapter.add("textAlign", function (textAlign, target) {
            return target.dataItem?.dataContext?.labelTextAlignment || "middle";
        });

        bullet.label.adapter.add("horizontalCenter", function (horizontalCenter, target) {
            return target.dataItem?.dataContext?.labelHorizontalCenter || "middle";
        });

        bullet.label.verticalCenter = "bottom";
        bullet.label.wrap = true;
        bullet.label.truncate = false;
        bullet.label.fontSize = 13;
        bullet.label.maxWidth = 400;
    });
};

/**
 * Adds arrows to chart based on shortcode parameters
 * Parses data-arrow0startX, data-arrow0startY, etc. parameters and calls addArrow for each
 *
 * @param {Object} chart - The chart object
 * @returns {void}
 */
bocAmchart4.addArrowsFromShortcode = function (chart) {
    let arrowIndex = 0;

    // Loop through arrows until we don't find the required startX parameter
    // Parameters are lowercased during processing, so use lowercase names
    while (chart.bocSettings.args.hasOwnProperty('arrow' + arrowIndex + 'startx')) {
        let prefix = 'arrow' + arrowIndex;

        // Required parameters
        let startX = chart.bocSettings.args[prefix + 'startx'];
        let startY = parseFloat(chart.bocSettings.args[prefix + 'starty']);
        let endX = chart.bocSettings.args[prefix + 'endx'];
        let endY = parseFloat(chart.bocSettings.args[prefix + 'endy']);

        // Optional parameters with defaults
        let lineType = chart.bocSettings.args[prefix + 'linetype'] || 'solid';
        let color = chart.bocSettings.args[prefix + 'color'] || 30;
        let hasArrow = chart.bocSettings.args[prefix + 'hasarrow'] !== 'false'; // Default true

        // Convert color to number if it's a string number
        if (typeof color === 'string' && !isNaN(color)) {
            color = parseInt(color);
        }

        // Convert startX and endX to numbers if they're numeric strings
        if (typeof startX === 'string' && !isNaN(startX) && startX.indexOf('-') === -1) {
            startX = parseFloat(startX);
        }
        if (typeof endX === 'string' && !isNaN(endX) && endX.indexOf('-') === -1) {
            endX = parseFloat(endX);
        }

        // Call addArrow with the parsed parameters
        bocAmchart4.addArrow(chart, startX, startY, endX, endY, lineType, color, hasArrow);

        arrowIndex++;
    }
};

/**
 * Adds labels to chart based on shortcode parameters
 * Parses data-label0x, data-label0y, etc. parameters and calls addLabels with collected data
 *
 * @param {Object} chart - The chart object
 * @returns {void}
 */
bocAmchart4.addLabelsFromShortcode = function (chart) {
    let labelIndex = 0;
    let labels = [];

    // Loop through labels until we don't find the required x parameter
    // Parameters are lowercased during processing, so use lowercase names
    while (chart.bocSettings.args.hasOwnProperty('label' + labelIndex + 'x')) {
        let prefix = 'label' + labelIndex;

        // Required parameters
        let x = chart.bocSettings.args[prefix + 'x'];
        let y = parseFloat(chart.bocSettings.args[prefix + 'y']);
        let text = chart.bocSettings.args[prefix + 'text'];

        // Optional parameters with defaults
        let horizontalCenter = chart.bocSettings.args[prefix + 'horizontalcenter'] || 'middle';
        let textAlignment = chart.bocSettings.args[prefix + 'textalignment'] || 'middle';

        // Convert x to number if it's a numeric string (but not a date)
        if (typeof x === 'string' && !isNaN(x) && x.indexOf('-') === -1) {
            x = parseFloat(x);
        }

        // Create label object
        let labelObj = {
            x: x,
            y: y,
            text: text,
            horizontalCenter: horizontalCenter,
            textAlignment: textAlignment
        };

        labels.push(labelObj);
        labelIndex++;
    }

    // If we found any labels, add them all at once using the existing addLabels function
    if (labels.length > 0) {
        bocAmchart4.addLabels(chart, labels);
    }
};
